import {
  WSFilters,
  WSInfiniteScroll,
  WSList,
  WSTableColumnSortDirection
} from "@wingspanhq/fe-component-library";
import { useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useUserId } from "../../../../query/hooks/helpers";
import { useQueryWorkLogs } from "../../../../query/workLog/queries/useQueryWorkLogs";
import { useQueryWorkLogsSummary } from "../../../../query/workLog/queries/useQueryWorkLogsSummary";
import { WSQueries } from "../../../../query/WSQuery";
import { useUrlQuery } from "../../../../shared/hooks/useUrlQuery";
import { usePayablesMap } from "../../hooks/usePayablesMap";
import { usePayeesMap } from "../../hooks/usePayeesMap";
import { getPathWorkLogPayerDetails } from "../../paths";
import {
  WorkLogsFilterEmptyState,
  WorkLogsPayerEmptyState
} from "../WorkLogsEmptyStates";
import { useBuildConfig } from "./filters/config";
import { createListRequest } from "./filters/request";
import { Filter, SortField } from "./filters/types";
import { TableWorkLogsPayerView } from "./TableWorkLogsPayerView";

type Props = {
  status?: Filter["status"];
};

export const FilterableTableWorkLogsPayerView: React.FC<Props> = props => {
  const userId = useUserId();
  const history = useHistory();

  const { urlQuery, setUrlQuery } = useUrlQuery<Filter, SortField>();

  const request = useMemo(
    () =>
      createListRequest(
        userId,
        {
          status: props.status,
          ...urlQuery.filter
        },
        urlQuery.sort
      ),
    [userId, props.status, urlQuery.filter, urlQuery.sort]
  );

  const filtersConfig = useBuildConfig();

  const queryWorkLogs = useQueryWorkLogs(request);
  const queryWorkLogsSummary = useQueryWorkLogsSummary(request);

  const payeesMap = usePayeesMap(queryWorkLogs.data);
  const { payablesMap } = usePayablesMap(queryWorkLogs.data);

  const hasFilters = useMemo(
    () => Object.keys(urlQuery.filter || {}).length > 0,
    [urlQuery.filter]
  );

  const sortByUpdatedAt = useCallback(
    (newDirection: WSTableColumnSortDirection) => {
      console.log("newDirection", newDirection);
      setUrlQuery(prev => ({
        ...prev,
        sort: {
          field: "updatedAt",
          direction: newDirection
        }
      }));
    },
    [setUrlQuery]
  );

  return (
    <WSList gap="2XL">
      <WSFilters<Filter>
        values={urlQuery.filter}
        onFilter={newValue => {
          setUrlQuery({ filter: newValue });
        }}
        primaryFilters={filtersConfig}
        info={{
          count: queryWorkLogsSummary.data?.listSize || 0,
          amount: queryWorkLogsSummary.data?.totalAmount
        }}
      />

      <WSQueries queries={{ queryWorkLogs }}>
        {({ queryWorkLogsData }) =>
          hasFilters && queryWorkLogsData.length === 0 ? (
            <WorkLogsFilterEmptyState />
          ) : queryWorkLogsData.length === 0 ? (
            <WorkLogsPayerEmptyState status={props.status} />
          ) : (
            <WSInfiniteScroll
              mt="M"
              onLoad={() => queryWorkLogs.fetchNextPage()}
              loadMore={queryWorkLogsData.length > 0}
              endOfList={!queryWorkLogs.hasNextPage}
              loading={!!queryWorkLogs.isFetchingNextPage}
            >
              <TableWorkLogsPayerView
                workLogs={queryWorkLogsData}
                payeesMap={payeesMap}
                payablesMap={payablesMap}
                onRowClick={workLog => {
                  history.push(getPathWorkLogPayerDetails(workLog.workLogId));
                }}
                onSortByUpdatedAt={sortByUpdatedAt}
                updatedAtDirection={urlQuery.sort?.direction}
              />
            </WSInfiniteScroll>
          )
        }
      </WSQueries>
    </WSList>
  );
};
